import { useCallback, useMemo, useState } from "react";
import { Chart, ChartTypes } from "@uicore";
import {
  Card,
  CardContent,
  CardHeader,
  Skeleton,
  Stack,
  Typography,
  NativeSelect,
} from "@altimateai/lego";
import { Bar } from "recharts";
import { ModelServingEndpointTrendPoint } from "../types";
import { formatCost } from "@/helpers/utils";
import { formatNumber } from "@/uiCore/components/formatters/format";
import { useAppState } from "@/modules/app/useAppContext";
import {
  type MetricType,
  formatDate,
  detectGranularity,
  GRANULARITY_LABELS,
  METRIC_COLORS,
} from "../utils";

const METRIC_EXTRACTORS: Record<MetricType, (p: ModelServingEndpointTrendPoint) => number> = {
  cost: p => p.cost_usd,
  requests: p => p.requests,
  tokens: p => p.input_tokens + p.output_tokens,
  users: p => p.unique_users,
};

const METRIC_TITLE: Record<MetricType, string> = {
  cost: "Cost Trend",
  requests: "Requests Trend",
  tokens: "Tokens Trend",
  users: "Unique Users Trend",
};

interface Props {
  data: ModelServingEndpointTrendPoint[];
  isLoading?: boolean;
  entityType?: string | null;
}

export const ModelServingEndpointTrendChart = ({ data, isLoading, entityType }: Props) => {
  const { currency } = useAppState();
  const isAiFunction = entityType === "AI_FUNCTION";
  const [metricType, setMetricType] = useState<MetricType>("cost");

  const granularity = useMemo(() => detectGranularity(data), [data]);

  const formatValue = useCallback(
    (value: number): string =>
      metricType === "cost" ? formatCost(value, currency) : formatNumber(value),
    [metricType, currency]
  );

  const chartData = useMemo(
    () =>
      data.map(point => ({
        date: point.date,
        value: METRIC_EXTRACTORS[metricType](point),
      })),
    [data, metricType]
  );

  const periodLabel = GRANULARITY_LABELS[granularity];
  const chartTitle = `${periodLabel} ${METRIC_TITLE[metricType]}`;

  return (
    <Card>
      <CardHeader>
        <Stack direction="row" align="center" justify="between" className="al-w-full">
          <Typography variant="section-heading">{chartTitle}</Typography>
          <Stack direction="row" align="center" gap="xs">
            <Typography size="sm" className="al-text-gray-500">
              Metric
            </Typography>
            <NativeSelect
              aria-label="Select metric"
              value={metricType}
              onChange={(value: string) => {
                const valid: MetricType[] = ["cost", "requests", "tokens", "users"];
                if (valid.includes(value as MetricType)) {
                  setMetricType(value as MetricType);
                }
              }}
              variant="outline"
              size="sm"
              options={[
                { value: "cost", label: "Cost" },
                { value: "requests", label: "Requests" },
                ...(!isAiFunction ? [{ value: "tokens", label: "Tokens" }] : []),
                { value: "users", label: "Users" },
              ]}
            />
          </Stack>
        </Stack>
      </CardHeader>
      <CardContent>
        <div className="al-pb-10">
          {isLoading ? (
            <Skeleton className="al-h-[380px] al-w-full" />
          ) : data.length === 0 ? (
            <Stack direction="column" align="center" justify="center" className="al-h-[380px]">
              <Typography className="al-text-gray-500">
                No trend data for selected period
              </Typography>
            </Stack>
          ) : (
            <Chart
              title=""
              xAxisDataKey="date"
              yAxisLabel={{
                value:
                  metricType === "cost"
                    ? "Cost"
                    : metricType.charAt(0).toUpperCase() + metricType.slice(1),
              }}
              xAxisLabelFormatter={(value: string) => formatDate(value, granularity)}
              yAxisLabelFormatter={(value: string) => formatValue(Number(value))}
              type={ChartTypes.BarChart}
              data={chartData}
              height={380}
              legendProps={{ wrapperStyle: { bottom: -16, paddingTop: 4, fontSize: 12 } }}
            >
              {({ barSize }) => (
                <Bar
                  barSize={barSize}
                  name={metricType.charAt(0).toUpperCase() + metricType.slice(1)}
                  dataKey="value"
                  fill={METRIC_COLORS[metricType]}
                  radius={[4, 4, 0, 0]}
                />
              )}
            </Chart>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
